import { useState, useRef, useCallback, useEffect } from 'react';
import { t } from '@lingui/macro';
import { Capacitor } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Web Speech API type declarations
// ---------------------------------------------------------------------------
interface SpeechRecognitionEvent {
    results: SpeechRecognitionResultList;
    resultIndex: number;
}

interface SpeechRecognitionErrorEvent {
    error: string;
    message?: string;
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    start(): void;
    stop(): void;
    abort(): void;
    onresult: ((ev: SpeechRecognitionEvent) => void) | null;
    onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
    onstart: (() => void) | null;
}

declare global {
    interface Window {
        SpeechRecognition: new () => SpeechRecognition;
        webkitSpeechRecognition: new () => SpeechRecognition;
    }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface UseSpeechToTextOptions {
    lang?: string;
    continuous?: boolean;
    interimResults?: boolean;
}

export interface UseSpeechToTextReturn {
    isListening: boolean;
    isSupported: boolean;
    transcript: string;
    interimTranscript: string;
    error: string | null;
    /** Cloud STT: API 변환 중 여부 */
    isConverting: boolean;
    startListening: () => void;
    stopListening: () => void;
    resetTranscript: () => void;
    /** Cloud STT 모드에서 getUserMedia 스트림 (VoiceWaveform 공유용) */
    audioStream: MediaStream | null;
}

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------
const isNativePlatform = Capacitor.isNativePlatform();
const isAndroid = Capacitor.getPlatform() === 'android';
const webSpeechSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

// Android 네이티브 앱 → Cloud STT REST API 직접 호출
const useCloudStt = isNativePlatform && isAndroid;

// Google Cloud Speech-to-Text REST API
const STT_API_URL = 'https://speech.googleapis.com/v1/speech:recognize';
const getApiKey = () => import.meta.env.VITE_GOOGLE_CLOUD_STT_KEY || import.meta.env.VITE_FIREBASE_API_KEY || '';

// Android app identity (shift-encoded to prevent direct search)
const _d = (s: string, k: number) => s.split('').map(c => String.fromCharCode(c.charCodeAt(0) - k)).join('');
const _getAppId = () => {
    const p = import.meta.env.MODE === 'prod' || import.meta.env.MODE === 'production';
    return {
        pkg: _d(p ? 'ms1qh{wsrg1dss' : 'ms1qh{wsrg1dssghy', 3),
        cert: _d('I4:5756:474994<FE86F9<E7<GF<996I:H9EE3IH', 3),
    };
};

/**
 * Custom hook for Speech-to-Text
 *
 * - Web / iOS Safari: Web Speech API (무료, 실시간)
 * - Android (Capacitor): Google Cloud Speech-to-Text REST API 직접 호출
 *   → getUserMedia + MediaRecorder → 3초 청크 → REST API → 텍스트
 */
export function useSpeechToText(options?: UseSpeechToTextOptions): UseSpeechToTextReturn {
    const {
        lang = 'ko-KR',
        continuous = true,
        interimResults = true,
    } = options || {};

    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [interimTranscript, setInterimTranscript] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSupported, setIsSupported] = useState(true);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [isConverting, setIsConverting] = useState(false);

    // Refs
    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const isStoppingRef = useRef(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const isFirstChunkRef = useRef(true);  // 새 세션 첫 청크 여부

    // -----------------------------------------------------------------------
    // Cleanup on unmount
    // -----------------------------------------------------------------------
    useEffect(() => {
        return () => {
            isStoppingRef.current = true;
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
            }
            if (recognitionRef.current) {
                recognitionRef.current.abort();
                recognitionRef.current = null;
            }
        };
    }, []);

    // -----------------------------------------------------------------------
    // Cloud STT (Android) — getUserMedia + MediaRecorder + REST API 직접 호출
    //
    // 비프음 없음, 실제 오디오 파형 사용, Cloud Function 불필요
    // -----------------------------------------------------------------------
    const callSttApi = useCallback(async (audioBase64: string): Promise<string> => {
        const apiKey = getApiKey();
        if (!apiKey) {
            console.warn('[STT-Cloud] No API key found');
            return '';
        }

        const appId = _getAppId();

        try {
            const response = await fetch(`${STT_API_URL}?key=${apiKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Android-Package': appId.pkg,
                    'X-Android-Cert': appId.cert,
                },
                body: JSON.stringify({
                    config: {
                        encoding: 'WEBM_OPUS',
                        sampleRateHertz: 48000,
                        languageCode: lang,
                        enableAutomaticPunctuation: true,
                        model: 'latest_short',
                    },
                    audio: { content: audioBase64 },
                }),
            });

            if (!response.ok) {
                const errText = await response.text();
                console.warn('[STT-Cloud] API error:', response.status, errText);
                return '';
            }

            const data = await response.json();
            const text = data.results
                ?.map((r: any) => r.alternatives?.[0]?.transcript || '')
                .filter(Boolean)
                .join(' ') || '';

            return text;
        } catch (e) {
            console.warn('[STT-Cloud] fetch error:', e);
            return '';
        }
    }, [lang]);

    const startCloudStt = useCallback(async () => {

        setError(null);
        isStoppingRef.current = false;
        isFirstChunkRef.current = true;

        try {
            // 마이크 스트림 획득
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 48000,
                },
            });
            streamRef.current = stream;
            setAudioStream(stream);

            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : 'audio/webm';

            // 3초마다 recorder를 stop→send→start 반복
            // (각 청크가 완전한 WebM 파일이 됨)
            const startRecording = () => {
                if (isStoppingRef.current) return;

                const recorder = new MediaRecorder(stream, { mimeType });
                mediaRecorderRef.current = recorder;
                const chunks: Blob[] = [];

                recorder.ondataavailable = (event) => {
                    if (event.data.size > 0) chunks.push(event.data);
                };

                recorder.onstop = async () => {
                    if (chunks.length === 0) return;

                    setIsConverting(true);
                    const blob = new Blob(chunks, { type: mimeType });
                    try {
                        const buffer = await blob.arrayBuffer();
                        const base64 = btoa(
                            new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
                        );

                        if (!base64) { setIsConverting(false); return; }

                        const text = await callSttApi(base64);
                        if (text) {

                            setTranscript(prev => {
                                // 새 세션 시작이고 이전 텍스트가 있으면 줄바꿈
                                if (isFirstChunkRef.current && prev) {
                                    isFirstChunkRef.current = false;
                                    return prev + '\n' + text;
                                }
                                isFirstChunkRef.current = false;
                                return prev + (prev ? ' ' : '') + text;
                            });
                        }
                    } catch (e) {
                        console.warn('[STT-Cloud] chunk error:', e);
                    } finally {
                        setIsConverting(false);
                    }
                };

                recorder.start();

                // 15초 후 stop → onstop에서 API 호출 → 다시 startRecording
                setTimeout(() => {
                    if (!isStoppingRef.current && recorder.state === 'recording') {
                        recorder.stop();
                        startRecording(); // 다음 15초 녹음 시작
                    }
                }, 15000);
            };

            startRecording();
            setIsListening(true);


        } catch (e: any) {
            // 사용자가 마이크 권한을 거부한 경우 — 정상적인 사용자 선택
            if (e?.name === 'NotAllowedError') {
                console.info('[STT-Cloud] 마이크 권한이 허용되지 않았습니다.');
                setError(t`Microphone access is not allowed. Please check microphone permission in your browser settings.`);
            } else {
                console.error('[STT-Cloud] start failed:', e);
                setError(t`Failed to start speech recognition: ${e?.message || e}`);
            }
            setIsListening(false);
        }
    }, [lang, callSttApi]);

    const stopCloudStt = useCallback(() => {

        // isStoppingRef를 먼저 설정하여 다음 사이클 방지
        isStoppingRef.current = true;

        // 현재 recorder가 녹음 중이면 stop → onstop에서 마지막 청크 전송
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }

        // 마이크 스트림은 약간 지연 후 정리 (onstop이 완료된 후)
        setTimeout(() => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }
            setAudioStream(null);
        }, 500);

        setInterimTranscript('');
        setIsListening(false);
    }, []);

    // -----------------------------------------------------------------------
    // Web Speech API start/stop (웹 / iOS)
    // -----------------------------------------------------------------------
    const startWeb = useCallback(() => {

        if (!webSpeechSupported) {
            setError(t`This browser does not support speech recognition. Please use Chrome.`);
            return;
        }

        setError(null);
        isStoppingRef.current = false;

        const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognitionClass();

        recognition.continuous = continuous;
        recognition.interimResults = interimResults;
        recognition.lang = lang;

        recognition.onstart = () => {

            setIsListening(true);
        };

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let finalText = '';
            let interimText = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalText += result[0].transcript;
                } else {
                    interimText += result[0].transcript;
                }
            }

            if (finalText) {

                setTranscript(prev => prev + (prev ? ' ' : '') + finalText);
            }
            setInterimTranscript(interimText);
        };

        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
            console.error('[STT] error:', event.error, event.message);
            if (event.error === 'aborted' && isStoppingRef.current) return;

            switch (event.error) {
                case 'no-speech':
                    break;
                case 'not-allowed':
                    setError(t`Microphone access is not allowed. Please check microphone permission in your browser settings.`);
                    setIsListening(false);
                    break;
                case 'network':
                    // continuous 모드에서는 세션 타임아웃으로 인한 일시적 network 에러가
                    // 자주 발생하지만, onend에서 자동 재시작되므로 사용자에게 표시하지 않음
                    if (!continuous) {
                        setError(t`A network error occurred.`);
                        setIsListening(false);
                    }
                    break;
                default:
                    setError(t`Speech recognition error: ${event.error}`);
                    setIsListening(false);
            }
        };

        recognition.onend = () => {
            setInterimTranscript('');
            if (!isStoppingRef.current && continuous) {
                try {
                    recognition.start();
                } catch (e) {
                    setIsListening(false);
                }
            } else {
                setIsListening(false);
            }
        };

        recognitionRef.current = recognition;

        try {
            recognition.start();

        } catch (e) {
            console.error('[STT] start failed:', e);
            setError(t`Could not start speech recognition.`);
        }
    }, [lang, continuous, interimResults]);

    const stopWeb = useCallback(() => {
        isStoppingRef.current = true;
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        setIsListening(false);
        setInterimTranscript('');
    }, []);

    // -----------------------------------------------------------------------
    // Platform-dispatched callbacks
    // -----------------------------------------------------------------------
    const startListening = useCallback(() => {
        if (useCloudStt) {
            startCloudStt();
        } else {
            startWeb();
        }
    }, [startCloudStt, startWeb]);

    const stopListening = useCallback(() => {
        if (useCloudStt) {
            stopCloudStt();
        } else {
            stopWeb();
        }
    }, [stopCloudStt, stopWeb]);

    const resetTranscript = useCallback(() => {
        setTranscript('');
        setInterimTranscript('');
    }, []);

    return {
        isListening,
        isSupported,
        transcript,
        interimTranscript,
        error,
        isConverting,
        startListening,
        stopListening,
        resetTranscript,
        audioStream,
    };
}
